import React, { useState, useRef, useEffect } from 'react';
import { useJobStore, Job } from '../../stores/jobStore';
import { useNavigate } from 'react-router-dom';

const JobBell: React.FC = () => {
  const { jobs, fetchJobs, startPolling, clearCompleted } = useJobStore();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<Job | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const activeCount = jobs.filter((j) => j.status === 'queued' || j.status === 'running').length;

  // Fetch jobs on mount and start polling if there are active ones
  useEffect(() => {
    fetchJobs().then(() => {
      const hasActive = useJobStore.getState().jobs.some(
        (j) => j.status === 'queued' || j.status === 'running'
      );
      if (hasActive) startPolling();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for job-finished events
  useEffect(() => {
    const handler = (e: Event) => {
      const job = (e as CustomEvent).detail as Job;
      setToast(job);
      setTimeout(() => setToast(null), 6000);
    };
    window.addEventListener('job-finished', handler);
    return () => window.removeEventListener('job-finished', handler);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fmtTime = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const statusColor = (s: string) => {
    if (s === 'completed') return '#16a34a';
    if (s === 'failed') return '#dc2626';
    if (s === 'running') return '#f59e0b';
    return '#6b7280';
  };

  const typeLabel = (t: string) => {
    const map: Record<string, string> = { docking: 'Docking', conformer: 'Conformer', pkpd_fit: 'PKPD Fit' };
    return map[t] || t;
  };

  return (
    <>
      {/* Toast notification */}
      {toast && (
        <div style={toastStyle}>
          <div style={{ fontWeight: 600, fontSize: 13, color: toast.status === 'completed' ? '#16a34a' : '#dc2626' }}>
            {typeLabel(toast.job_type)} {toast.status === 'completed' ? 'complete' : 'failed'}
          </div>
          {toast.status === 'completed' && (
            <button
              onClick={() => {
                setToast(null);
                setOpen(true);
              }}
              style={{ fontSize: 12, color: '#f59e0b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              View results
            </button>
          )}
          {toast.error_message && (
            <div style={{ fontSize: 11, color: '#fb7185', marginTop: 2 }}>{toast.error_message}</div>
          )}
        </div>
      )}

      {/* Bell icon with dropdown */}
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          className="liquid-glass-header__theme-toggle"
          onClick={() => { setOpen(!open); if (!open) fetchJobs(); }}
          title="Background jobs"
          aria-label="Background jobs"
          style={{ position: 'relative' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {activeCount > 0 && (
            <span style={badgeStyle}>{activeCount}</span>
          )}
        </button>

        {open && (
          <div style={dropdownStyle}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--card-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>Background Jobs</span>
              {jobs.some((j) => j.status === 'completed' || j.status === 'failed') && (
                <button onClick={clearCompleted} style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Clear
                </button>
              )}
            </div>

            {jobs.length === 0 ? (
              <div style={{ padding: '16px 12px', color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>
                No jobs yet
              </div>
            ) : (
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                {jobs.slice(0, 10).map((job) => (
                  <div key={job.id} style={jobRowStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{typeLabel(job.job_type)}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: statusColor(job.status), textTransform: 'uppercase' }}>{job.status}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {job.status === 'running' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <span style={miniSpinnerStyle} /> Processing...
                        </span>
                      )}
                      {job.status === 'queued' && 'Waiting in queue'}
                      {job.status === 'completed' && `Finished at ${fmtTime(job.completed_at)}`}
                      {job.status === 'failed' && (job.error_message || 'Failed')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const toastStyle: React.CSSProperties = {
  position: 'fixed', top: 56, right: 16, zIndex: 10001,
  background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 10, padding: '10px 14px', minWidth: 200, maxWidth: 320,
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  animation: 'slideInRight 0.3s ease',
};

const badgeStyle: React.CSSProperties = {
  position: 'absolute', top: -4, right: -4,
  background: '#f59e0b', color: '#000', fontSize: 10, fontWeight: 700,
  width: 16, height: 16, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  lineHeight: 1,
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', right: 0, marginTop: 8,
  background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 10, width: 300, zIndex: 10000,
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
};

const jobRowStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--card-border)',
};

const miniSpinnerStyle: React.CSSProperties = {
  width: 10, height: 10,
  border: '2px solid var(--card-border)', borderTopColor: '#f59e0b',
  borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  display: 'inline-block',
};

export default JobBell;
